import { defineComponent as _defineComponent } from 'vue'
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { WatchlistTitleGraphqlFragment } from '@/components/watchlist/graphql/fragments/WatchlistTitle.fragment';

import GridBuyBox from '@/features/buybox/components/grid/GridBuyBox.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import TitleScoreList from '@/components/TitleScoreList.vue';

import { TitleListType, CreativeType } from '@/@types/graphql-types';
import { CollectionType } from '@/enums/collection-type';

import { isTitleInList } from '@/helpers/title-helper';

import { useMounted } from '@vueuse/core';
import { computed, ref } from 'vue';
import { PosterSponsoredRecImages } from '@/interfaces/components/poster/PosterGraphql';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleListRow',
  props: {
    titles: { default: () => [] },
    isAtEndOfList: { type: Boolean },
    hasNoTitles: { type: Boolean },
    loading: { type: Boolean },
    isSeoSRLoaded: { type: Boolean },
    sponsoredAd: null,
    collectionType: null
  },
  emits: ["onPosterClick", "load-next", "clickout"],
  setup(__props: any, { expose }: { emit: ({
	(e: 'onPosterClick', data: { title: WatchlistTitleGraphqlFragment['node']; index: number }): void;
	(e: 'load-next', data: { event: any; paginationToken: null }): void;
	(e: 'clickout', titleContext: SnowplowTitleContextGraphql): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { titles: WatchlistTitleGraphqlFragment[], isAtEndOfList?: boolean, hasNoTitles?: boolean, loading?: boolean, isSeoSRLoaded?: boolean, sponsoredAd?: SponsoredAdFragment, collectionType?: CollectionType };





const { isSeoUser } = storeToRefs(useAppStore());
const { activeRoute } = storeToRefs(useRoutingStore());
const { isLoggedIn } = storeToRefs(useUserStore());
const { country } = storeToRefs(useLanguageStore());

const isMounted = useMounted();

const showQualityTv = computed(() => ['US', 'GB'].includes(country.value) && !isLoggedIn.value);

const disableInfiniteScroll = computed(() => {
	const isTitleDetailPage = activeRoute.value?.name?.includes('app.detail');
	return props.hasNoTitles || props.isAtEndOfList || isTitleDetailPage;
});

const sponsoredAdPath = computed(() => {
	if (!props.sponsoredAd?.campaign) return;

	type SRNode = AssertByTypename<typeof props.sponsoredAd.campaign.node, 'Movie' | 'Show'>;
	return (props.sponsoredAd?.campaign?.node as SRNode)?.content?.fullPath;
});

const sponsoredAdCampaign = computed(() => props.sponsoredAd?.campaign);

const posterSRImageOverride = computed<PosterSponsoredRecImages | null>(() => {
	let posterUrl = props.sponsoredAd?.campaign?.posterOverride;
	if (!posterUrl) return null;

	if (posterUrl.startsWith('/')) {
		posterUrl = `${JW_CONFIG.IMAGESCALER_URL}${posterUrl.replace('{profile}', 's332')}`;
	}

	return {
		imageUrl: posterUrl,
		sourceSets: [
			{
				srcSet: posterUrl.replace(/\.{?\w+}?$/, '.avif'),
				imageProp: {
					extension: '.avif',
					type: 'image/avif',
				},
				sourceProp: {
					viewport: 'desktop',
				},
				media: null,
			},
		],
	};
});

const hasActiveSRCampaign = computed(() => {
	return props.sponsoredAd?.campaign != null && props.sponsoredAd?.holdoutGroup == false;
});

const isListSr = computed(() => props.sponsoredAd?.campaign?.node?.__typename === 'GenericTitleList');

const isSearchPage = computed(() => props.collectionType === CollectionType.SEARCH);

// For now, poster next to the SR is migrated to native long rectangle only for Image creative
// For video poster remains the same to the SRs left side till also migrated to native long rectangle video
const isImageCreativeSr = computed(() => props.sponsoredAd?.campaign?.creativeType === CreativeType.Image);

const isVideoCreativeSr = computed(
	// scope only to US for now
	() =>
		//country.value === 'US' &&
		props.sponsoredAd?.campaign?.creativeType === CreativeType.Video
);

function lazyPoster(index: number) {
	return BUILD_CONFIG.globals.BUILD_TARGET === 'SSR' ? index >= IMG_POSTER_LOAD_COUNT : true;
}

const infiniteScroll = ref<InstanceType<typeof InfiniteScroll> | null>(null);

// Resets the internal state of the infinite scroll. Needs to be called at each filter change.
function resetInfiniteScroll() {
	infiniteScroll.value?.reset();
}

expose({ resetInfiniteScroll });

return { __sfc: true,props, isSeoUser, activeRoute, isLoggedIn, country, isMounted, showQualityTv, disableInfiniteScroll, sponsoredAdPath, sponsoredAdCampaign, posterSRImageOverride, hasActiveSRCampaign, isListSr, isSearchPage, isImageCreativeSr, isVideoCreativeSr, lazyPoster, infiniteScroll, resetInfiniteScroll, GridBuyBox, InfiniteScroll, PosterGraphql, TitleScoreList, TitleListType, isTitleInList }
}

})